import { KeyboardEvent, RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { CustomMusic, Hymn, isSongFileMissing, SongFileExtension, SongSlug } from '../../../../../common/src/model.ts';
import { SypMusicFileKey } from '../../../../../common/src/syp_file.ts';
import { formatVerses, getPartialVerses, normalizeVerses } from '../../../../../common/src/verses.ts';
import { HymnalWithHymns } from '../../../data/hymnals.ts';
import { getHymnFromSlug, getSypUrlForHymn } from '../../../util/path.ts';
import {
  nameBeforeSuffix,
  nameWithSuffixRow,
  suffixAfterNameInFillingRow,
  suffixRoom,
} from '../../../views/name_with_suffix.ts';
import {
  clampToViewport,
  onViewportChange,
  PopupBackdrop,
  popupZIndex,
  portalEventGuards,
  useDismiss,
} from './popup.tsx';

// The picker Choose verses… opens under a song cell (issue #4169): one button per stanza, All, a preview of how the
// cell will read, and Done. Every stanza is the default, so when the song's stanza count is known (from its manifest or
// its .syp file, never the fallback below) choosing every stanza stores no verses and the preview shows the bare song,
// and All means every stanza: it chooses exactly 1 to the count, dropping any higher verse the cell held, so All then
// Done stores no verses (issue #4212). While the count is unknown, All ticks every button shown and Done keeps whatever
// was chosen; when the count arrives after All with no stanza pressed since, All chooses 1 to the count after all. No
// stanza ticked stores no verses: unticking them all is how verses are cleared.

export interface VersesPickerProps {
  // the cell the picker opens under (above it when there is no room below)
  anchorRef: RefObject<HTMLElement | null>;
  songSlug: SongSlug;
  songName: string;
  verses?: number[];
  hymnals?: HymnalWithHymns[];
  customMusic?: CustomMusic;
  // the chosen verses, ascending; undefined when none are chosen
  onDone: (verses: number[] | undefined) => void;
  onCancel: () => void;
  // for Storybook only: render in place, in the page flow, instead of floating over the page under anchorRef
  isInline?: boolean;
}

// the stanzas offered while the song's own count is unknown
const fallbackVerseCount = 8;
// how long the row of stanzas stays invisible while the song's file is on its way, so it does not draw the fallback
// count and then change to the song's own (the file usually arrives within a tenth of a second)
const maxStanzaCountWait = 300;
// the most stanza buttons the picker shows: a pasted 'vv. 1–999' must not make 999 of them. A chosen stanza above it
// has no button, and stays chosen (the preview shows it, Done keeps it) until the buttons are pressed and none is left
// ticked: then Done stores no verses at all. All on a song with more stanzas than this still chooses every one of them,
// so Done stores no verses, as on any song whose count is known.
const maxPickerStanzas = 30;
// wider than the menus' viewportMargin: the picker is a larger card with a deep shadow, and on a phone its max-width
// leaves this much on either side
const pickerViewportMargin = 16;
const anchorGap = 6;

export const VersesPicker = ({
  anchorRef,
  songSlug,
  songName,
  verses,
  hymnals,
  customMusic,
  onDone,
  onCancel,
  isInline,
}: VersesPickerProps) => {
  const pickerRef = useRef<HTMLDivElement>(null);
  const [pressedSelection, setPressedSelection] = useState<number[]>(() => normalizeVerses(verses) ?? []);
  // whether a stanza or All has been pressed: until then Done keeps the cell's verses, even ones above the buttons
  const [isTouched, setIsTouched] = useState(false);
  // All was the last button pressed: it means every stanza, also when it was pressed before the song's count arrived
  // and the fallback buttons were all it could tick (issue #4212), until a stanza is pressed
  const [isAllPressed, setIsAllPressed] = useState(false);
  const { verseCount, isPending } = useVerseCount(getHymnFromSlug({ songSlug, hymnals, customMusic }));
  const selection = isAllPressed && verseCount !== undefined ? getStanzasThrough(verseCount) : pressedSelection;
  const [isWaitOver, setIsWaitOver] = useState(false);
  useEffect(() => {
    if (!isPending) {
      return;
    }
    const timer = setTimeout(() => setIsWaitOver(true), maxStanzaCountWait);
    return () => clearTimeout(timer);
  }, [isPending]);
  const isStanzaRowHidden = isPending && !isWaitOver;
  const stanzaCount = Math.min(Math.max(verseCount ?? fallbackVerseCount, ...selection), maxPickerStanzas);
  const stanzas = getStanzasThrough(stanzaCount);

  // placed again when the row of stanzas grows (the song's count arrived), which can make it taller
  useLayoutEffect(() => {
    if (isInline) {
      return;
    }
    const place = () => {
      const picker = pickerRef.current;
      const anchor = anchorRef.current;
      if (!picker || !anchor) {
        return;
      }
      const cell = anchor.getBoundingClientRect();
      const { offsetWidth: width, offsetHeight: height } = picker;
      const below = cell.bottom + anchorGap;
      const above = cell.top - anchorGap - height;
      // above when it does not fit below; fitting on neither side, it is kept inside the window from above, flush with
      // the top margin, away from the bottom of a phone's screen, where the keyboard or a bottom bar would hide its
      // stanzas
      const top = below + height <= window.innerHeight - pickerViewportMargin ? below : above;
      picker.style.left = `${clampToViewport(cell.left, width, window.innerWidth, pickerViewportMargin)}px`;
      picker.style.top = `${clampToViewport(top, height, window.innerHeight, pickerViewportMargin)}px`;
    };
    place();
    const controller = new AbortController();
    onViewportChange(place, controller.signal);
    return () => controller.abort();
  }, [anchorRef, isInline, stanzaCount]);

  // in place, focusing would scroll the page to the picker
  useEffect(() => {
    if (!isInline) {
      pickerRef.current?.focus();
    }
  }, [isInline]);

  useDismiss(onCancel);

  const toggle = (stanza: number) => {
    setIsTouched(true);
    setIsAllPressed(false);
    setPressedSelection(
      selection.includes(stanza) ? selection.filter((verse) => verse !== stanza) : [...selection, stanza],
    );
  };

  // what All chooses: with the song's count known, every stanza and nothing else (a verse above the count, such as the
  // fallback 7 and 8 saved before the count was known, goes, and the row shrinks back to the count); while it is
  // unknown, every button shown, keeping the chosen stanzas above them, until the count arrives and selection reads
  // every stanza after all (isAllPressed)
  const pressAll = () => {
    setIsTouched(true);
    setIsAllPressed(true);
    setPressedSelection([...stanzas, ...selection.filter((verse) => verse > stanzaCount)]);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      onCancel();
    }
  };

  // what Done stores: the preview shows exactly this. Once a button has been pressed, an empty row of buttons means
  // nothing chosen, even when the selection holds a stanza above them; until then a cell holding only such stanzas
  // (a pasted 'vv. 31–33') keeps them
  const isAnyStanzaTicked = selection.some((verse) => verse <= stanzaCount);
  const chosenVerses = isTouched && !isAnyStanzaTicked ? undefined : getPartialVerses(selection, verseCount);
  const suffix = formatVerses(chosenVerses);

  const picker = (
    <Picker
      ref={pickerRef}
      role="dialog"
      aria-label="Verses"
      tabIndex={-1}
      $isInline={isInline}
      {...portalEventGuards}
      onKeyDown={onKeyDown}
    >
      <Heading>
        <Eyebrow>Verses</Eyebrow>
        <SongName>{songName}</SongName>
      </Heading>
      <Stanzas $isHidden={isStanzaRowHidden}>
        {stanzas.map((stanza) => (
          <Stanza key={stanza} type="button" aria-pressed={selection.includes(stanza)} onClick={() => toggle(stanza)}>
            {stanza}
          </Stanza>
        ))}
        <AllButton type="button" onClick={pressAll}>
          All
        </AllButton>
      </Stanzas>
      <Preview>
        <PreviewLabel>Shows as</PreviewLabel>
        <PreviewSongAndVerses>
          <PreviewName>{songName}</PreviewName>
          {suffix && <PreviewSuffix>{suffix}</PreviewSuffix>}
        </PreviewSongAndVerses>
      </Preview>
      <Footer>
        <DoneButton type="button" onClick={() => onDone(chosenVerses)}>
          Done
        </DoneButton>
      </Footer>
    </Picker>
  );
  if (isInline) {
    return picker;
  }
  return createPortal(
    <>
      <PopupBackdrop onDismiss={onCancel} />
      {picker}
    </>,
    document.body,
  );
};

/**
 * The number of stanzas the song has: the manifest's count when it has one, otherwise the count in the song's .syp
 * file, fetched once the picker opens. Undefined while unknown, and when the file is missing, unreachable or names
 * no count. isPending while the file is on its way.
 */
function useVerseCount(hymn: Hymn | undefined): { verseCount?: number; isPending: boolean } {
  const manifestCount = toVerseCount(hymn?.verseCount);
  const sypUrl =
    hymn && manifestCount === undefined && !isSongFileMissing(hymn, SongFileExtension.Syp)
      ? getSypUrlForHymn(hymn)
      : undefined;
  // the file whose fetch has settled, and the count it named, if any
  const [fetched, setFetched] = useState<{ url: string; count?: number }>();

  useEffect(() => {
    if (!sypUrl) {
      return;
    }
    const controller = new AbortController();
    fetchVerseCount(sypUrl, controller.signal)
      .then((count) => {
        if (!controller.signal.aborted) {
          setFetched({ url: sypUrl, count });
        }
      })
      .catch((error: unknown) => {
        // offline or a 404: the picker offers the fallback count (a picker that closed before the file arrived
        // aborted the fetch itself, which is not worth a log line)
        if (controller.signal.aborted) {
          return;
        }
        console.log(`Verse count unavailable for ${sypUrl}: ${String(error)}`);
        setFetched({ url: sypUrl });
      });
    return () => controller.abort();
  }, [sypUrl]);

  const isFetched = sypUrl !== undefined && fetched?.url === sypUrl;
  return {
    verseCount: manifestCount ?? (isFetched ? fetched.count : undefined),
    isPending: sypUrl !== undefined && !isFetched,
  };
}

async function fetchVerseCount(url: string, signal: AbortSignal): Promise<number | undefined> {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(response.statusText);
  }
  // one metadata line ('verse_count 6') near the top of the file: the measures that follow need no parsing. Key and
  // value are on one line, as parseSypFile reads them: a bare 'verse_count' does not take the next line's number. The
  // m flag's $ also matches before the \r of a file saved on Windows.
  const match = verseCountPattern.exec(await response.text());
  return toVerseCount(match ? Number(match[1]) : undefined);
}

const verseCountPattern = new RegExp(`^${SypMusicFileKey.VerseCount}[ \\t]+(\\d+)[ \\t]*$`, 'm');

// stanzas 1 to `count`
function getStanzasThrough(count: number): number[] {
  return Array.from({ length: count }, (_, index) => index + 1);
}

// a positive whole number of stanzas, or undefined (0 and NaN count as unknown)
function toVerseCount(count: number | undefined): number | undefined {
  return count !== undefined && Number.isInteger(count) && count > 0 ? count : undefined;
}

const Picker = styled.div<{ $isInline?: boolean }>`
  position: ${(props) => (props.$isInline ? 'static' : 'fixed')};
  z-index: ${popupZIndex};
  box-sizing: border-box;
  width: 372px;
  max-width: calc(100vw - 32px);
  padding: 16px 18px 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--color-background);
  color: var(--color-text);
  border: 1px solid var(--color-button-border);
  border-radius: 14px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.2);
  font-family: Jost-Regular, Arial, sans-serif;
  outline: none;
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Eyebrow = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-darker);
`;

const SongName = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// hidden, not removed, while the song's count is on its way: the picker keeps its height
const Stanzas = styled.div<{ $isHidden: boolean }>`
  visibility: ${(props) => (props.$isHidden ? 'hidden' : 'visible')};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const Stanza = styled.button`
  box-sizing: border-box;
  width: 36px;
  height: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid var(--color-button-border);
  border-radius: 18px;
  background: var(--color-background);
  color: var(--color-text);
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 15px;
  cursor: pointer;

  &[aria-pressed='true'] {
    background: var(--color-nightingale);
    border-color: var(--color-nightingale);
    color: var(--color-text-white);
  }
`;

// closes the row of stanzas, on whichever line it wraps to
const AllButton = styled.button`
  height: 30px;
  margin-left: auto;
  padding: 0 10px;
  border: 0;
  background: transparent;
  color: var(--color-nightingale);
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 13px;
  cursor: pointer;
`;

// the suffix's room (issue #4212): its width is the card's, never its contents', and the container sits here rather than
// on PreviewSongAndVerses, since a container may export no baseline and that row's aligns with the label's
const Preview = styled.div`
  ${suffixRoom}
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--color-background-search);
  font-size: 13px;
  white-space: nowrap;
`;

const PreviewLabel = styled.span`
  flex-shrink: 0;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-darker);
`;

// the song and the verses as the cell lays them out, filling the rest of the card's line: a suffix only a paste or the
// MCP tools could make ellipsizes inside the card (issue #4212)
const PreviewSongAndVerses = styled.span`
  ${nameWithSuffixRow}
  flex: 1;
  align-items: baseline;
  gap: 8px;
`;

const PreviewName = styled.span`
  ${nameBeforeSuffix}
  color: var(--color-date-highlight-text);
`;

const PreviewSuffix = styled.span`
  ${suffixAfterNameInFillingRow}
  color: var(--color-text-darker);
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const DoneButton = styled.button`
  height: 36px;
  padding: 0 22px;
  border: 0;
  border-radius: 10px;
  background: var(--color-primary-button);
  color: var(--color-text-white);
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 14px;
  cursor: pointer;
`;
